import { For, Show, Suspense, createSignal, createMemo, type ParentComponent } from "solid-js";
import { useParams, A, createAsync, revalidate, useAction, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { NoHydration } from "solid-js/web";
import { AppShell, PageHeader } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, Skeleton, EmptyState,
  Tabs, TabsList, TabsTrigger, TabsContent, Breadcrumbs
} from "~/components/ui";
import { GenerateReportModal } from "~/components/modals";
import { getTopic, getVideos, getPredictions, updateTopic } from "~/server/bff";
import { CACHE_KEYS } from "~/server/bff/cache-keys";
import { formatRelativeTime, formatDuration, formatDate } from "~/lib/utils";
import {
  Edit, Bell, BellOff, FileText, TrendingUp, Target, Link2,
  Video, ArrowLeft, BarChart3, Play
} from "lucide-solid";

export const route = {
  preload: ({ params }) => getTopic(params.id as string),
} satisfies RouteDefinition;

export default function TopicDetailPage() {
  const params = useParams();
  const [activeTab, setActiveTab] = createSignal("feed");
  const [showReportModal, setShowReportModal] = createSignal(false);
  const [isTogglingAlerts, setIsTogglingAlerts] = createSignal(false);

  const updateTopicAction = useAction(updateTopic);
  const topic = createAsync(() => getTopic(params.id || ""));
  
  // Derive the topic name for filtering
  const topicName = createMemo(() => topic()?.name);
  
  const videos = createAsync(() =>
    topicName() ? getVideos({ topicTag: topicName() }) : Promise.resolve([])
  );
  const predictions = createAsync(() =>
    topicName() ? getPredictions({ topicTag: topicName() }) : Promise.resolve([])
  );

  const handleToggleAlerts = async (topicId: string, alertsEnabled: boolean) => {
    setIsTogglingAlerts(true);
    try {
      const formData = new FormData();
      formData.append("id", topicId);
      formData.append("alertsEnabled", (!alertsEnabled).toString());
      await updateTopicAction(formData);
      await revalidate([
        getTopic.keyFor(params.id || ""),
        CACHE_KEYS.TOPICS,
        CACHE_KEYS.TRENDING_TOPICS,
      ]);
    } finally {
      setIsTogglingAlerts(false);
    }
  };

  return (
    <AppShell>
      <Title>{topic()?.name ?? "Topic"} - IntelAnalyst</Title>
      <div class="p-6">
        <Suspense fallback={<TopicHeaderSkeleton />}>
          <Show when={topic()}>
            {(t) => (
              <>
                <div class="flex flex-col gap-2 mb-6">
                  {/* Breadcrumbs */}
                  <Breadcrumbs
                    items={[
                      { label: "Explore", href: "/explore/search" },
                      { label: "Topics", href: "/explore/topics" },
                      { label: t().name },
                    ]}
                    showHome
                  />

                  <div class="flex items-start justify-between">
                    <div>
                      <div class="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">Topic</Badge>
                      </div>
                      <h1 class="text-2xl font-bold">{t().name}</h1>
                      <Show when={t().description}>
                        <p class="text-muted-foreground mt-1">{t().description}</p>
                      </Show>
                      <div class="flex flex-wrap gap-1 mt-3">
                        <For each={t().keywords}>
                          {(keyword) => (
                            <Badge variant="outline">{keyword}</Badge>
                          )}
                        </For>
                      </div>
                    </div>
                    <div class="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isTogglingAlerts()}
                        onClick={() => void handleToggleAlerts(t().id, t().alertsEnabled)}
                      >
                        <Show
                          when={t().alertsEnabled}
                          fallback={
                            <StaticIcon>
                              <BellOff class="h-4 w-4 mr-1" />
                            </StaticIcon>
                          }
                        >
                          <StaticIcon>
                            <Bell class="h-4 w-4 mr-1" />
                          </StaticIcon>
                        </Show>
                        {isTogglingAlerts() ? "..." : t().alertsEnabled ? "Alerts On" : "Alerts Off"}
                      </Button>
                      <A href={`/explore/topics/${t().id}/edit`}>
                        <Button variant="outline" size="sm">
                          <StaticIcon>
                            <Edit class="h-4 w-4 mr-1" />
                          </StaticIcon>
                          Edit
                        </Button>
                      </A>
                      <Button size="sm" onClick={() => setShowReportModal(true)}>
                        <StaticIcon>
                          <FileText class="h-4 w-4 mr-1" />
                        </StaticIcon>
                        Generate Report
                      </Button>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent class="p-4 flex items-center gap-3">
                      <StaticIcon>
                        <Video class="h-8 w-8 text-muted-foreground" />
                      </StaticIcon>
                      <div>
                        <p class="text-2xl font-bold">{t().videoCount}</p>
                        <p class="text-sm text-muted-foreground">Videos</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4 flex items-center gap-3">
                      <StaticIcon>
                        <Link2 class="h-8 w-8 text-muted-foreground" />
                      </StaticIcon>
                      <div>
                        <p class="text-2xl font-bold">{t().channelCount}</p>
                        <p class="text-sm text-muted-foreground">Creators</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4 flex items-center gap-3">
                      <StaticIcon>
                        <Target class="h-8 w-8 text-muted-foreground" />
                      </StaticIcon>
                      <div>
                        <p class="text-2xl font-bold">{predictions()?.length || 0}</p>
                        <p class="text-sm text-muted-foreground">Predictions</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4 flex items-center gap-3">
                      <StaticIcon>
                        <TrendingUp class="h-8 w-8 text-green-500" />
                      </StaticIcon>
                      <div>
                        <p class="text-2xl font-bold">+{t().recentActivity}</p>
                        <p class="text-sm text-muted-foreground">Recent</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </Show>
        </Suspense>

        <Tabs value={activeTab()} onChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="feed">
              <StaticIcon>
                <Video class="h-4 w-4 mr-1" />
              </StaticIcon>
              Feed
            </TabsTrigger>
            <TabsTrigger value="trends">
              <StaticIcon>
                <BarChart3 class="h-4 w-4 mr-1" />
              </StaticIcon>
              Trends
            </TabsTrigger>
            <TabsTrigger value="predictions">
              <StaticIcon>
                <Target class="h-4 w-4 mr-1" />
              </StaticIcon>
              Predictions
            </TabsTrigger>
            <TabsTrigger value="related">
              <StaticIcon>
                <Link2 class="h-4 w-4 mr-1" />
              </StaticIcon>
              Related
            </TabsTrigger>
          </TabsList>

          <TabsContent value="feed" class="mt-4">
            <Suspense fallback={<VideoListSkeleton />}>
              <Show when={videos()} fallback={<VideoListSkeleton />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={
                          <StaticIcon>
                            <Video class="h-12 w-12" />
                          </StaticIcon>
                        }
                        title="No videos yet"
                        description="Videos matching this topic will appear here"
                      />
                    }
                  >
                    <div class="space-y-3">
                      <For each={items()}>
                        {(video) => (
                          <A href={`/video/${video.id}`}>
                            <Card class="hover:bg-accent/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex gap-4">
                                  <div class="relative flex-shrink-0">
                                    <img
                                      src={video.thumbnailUrl}
                                      alt=""
                                      class="h-20 w-36 rounded object-cover"
                                    />
                                    <div class="absolute bottom-1 right-1 rounded bg-black/80 px-1 py-0.5 text-xs text-white">
                                      {formatDuration(video.duration)}
                                    </div>
                                  </div>
                                  <div class="flex-1 min-w-0">
                                    <h3 class="font-medium line-clamp-2">{video.title}</h3>
                                    <p class="text-sm text-muted-foreground mt-1">
                                      {video.channel?.name} • {formatRelativeTime(video.publishedAt)}
                                    </p>
                                    <Show when={video.summary}>
                                      <p class="text-sm text-muted-foreground mt-2 line-clamp-2">
                                        {video.summary}
                                      </p>
                                    </Show>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>

          <TabsContent value="trends" class="mt-4">
              <Card>
                <CardContent class="p-8 text-center">
                  <StaticIcon>
                    <BarChart3 class="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  </StaticIcon>
                  <div class="flex items-center justify-center gap-2 mb-2">
                    <h3 class="font-medium">Trend Charts</h3>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                <p class="text-sm text-muted-foreground">
                  View mention volume and creator coverage over time
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="predictions" class="mt-4">
            <Suspense fallback={<Skeleton class="h-64 w-full" />}>
              <Show when={predictions()} fallback={<Skeleton class="h-64 w-full" />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={
                          <StaticIcon>
                            <Target class="h-12 w-12" />
                          </StaticIcon>
                        }
                        title="No predictions"
                        description="Predictions related to this topic will appear here"
                      />
                    }
                  >
                    <div class="space-y-3">
                      <For each={items()}>
                        {(prediction) => (
                          <A href={`/track/predictions/${prediction.id}`}>
                            <Card class="hover:bg-accent/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex items-start justify-between">
                                  <div>
                                    <p class="font-medium">{prediction.event}</p>
                                    <p class="text-sm text-muted-foreground mt-1">
                                      {prediction.channel?.name}
                                    </p>
                                  </div>
                                  <Badge
                                    variant={
                                      prediction.status === "verified"
                                        ? "success"
                                        : prediction.status === "failed"
                                        ? "destructive"
                                        : "outline"
                                    }
                                  >
                                    {prediction.status}
                                  </Badge>
                                </div>
                                <Show when={prediction.targetDate}>
                                  <p class="text-xs text-muted-foreground mt-2">
                                    Due: {formatDate(prediction.targetDate!)}
                                  </p>
                                </Show>
                              </CardContent>
                            </Card>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>

          <TabsContent value="related" class="mt-4">
              <Card>
                <CardContent class="p-8 text-center">
                  <StaticIcon>
                    <Link2 class="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  </StaticIcon>
                  <div class="flex items-center justify-center gap-2 mb-2">
                    <h3 class="font-medium">Related Content</h3>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                <p class="text-sm text-muted-foreground">
                  Discover related topics and channels covering similar content
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Show when={showReportModal()}>
        <Suspense>
          <GenerateReportModal
            open={showReportModal()}
            onOpenChange={setShowReportModal}
            preselectedSourceType="topics"
            preselectedSourceId={params.id}
          />
        </Suspense>
      </Show>
    </AppShell>
  );
}

function TopicHeaderSkeleton() {
  return (
    <div class="space-y-4">
      <Skeleton class="h-8 w-48" />
      <Skeleton class="h-4 w-96" />
      <div class="grid grid-cols-4 gap-4">
        <Skeleton class="h-24" />
        <Skeleton class="h-24" />
        <Skeleton class="h-24" />
        <Skeleton class="h-24" />
      </div>
    </div>
  );
}

function VideoListSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex gap-4">
                <Skeleton class="h-20 w-36" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-3/4" />
                  <Skeleton class="h-4 w-1/2" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}

const StaticIcon: ParentComponent = (props) => {
  return <NoHydration>{props.children}</NoHydration>;
};
